import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { useDiscountTime } from '@/hooks/discount/useDiscountTime';
import { DiscountRateGuide } from '@/components/seller/discount/DiscountRateGuide';
import { TimeSettingSection } from '@/components/seller/discount/sections/TimeSettingSection';
import { DiscountSaveButton } from '@/components/seller/discount/DiscountSaveButton';
import { EcoPackTemplateSection, EcoPackTemplate } from '@/components/seller/discount/sections/EcoPackTemplateSection';
import { calculateDefaultPickupTimes } from '@/services/seller/discount/discount-defaults';
import { isValidTimeRange as isValidTimeRangeFn, generateTimeOptions } from '@/services/seller/discount/discount-time';
import { batchUpsertProductOffers, setOffersActiveForProducts } from '@/services/seller/discount/discount-offers';
import { upsertProductStock } from '@/services/seller/seller-helpers';
import { getKoreanDate } from '@/lib/dateUtils';
import { DEFAULT_ECOPACK_IMAGE_URL, LEGACY_ECOPACK_IMAGE_URL } from '@/constants';

export function DesktopDeadlineDiscountPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [defaultPickupTimes, setDefaultPickupTimes] = useState({
    start: '18:00',
    end: '20:00'
  });
  const [templates, setTemplates] = useState<EcoPackTemplate[]>([]);
  const [removedTemplateIds, setRemovedTemplateIds] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [useCustomOrderWindow, setUseCustomOrderWindow] = useState(false);
  const [customOrderStartTime, setCustomOrderStartTime] = useState('');
  const [customOrderEndTime, setCustomOrderEndTime] = useState('');
  const defaultDiscountRate = 40;
  const createClientId = () => `client-${Math.random().toString(36).slice(2, 10)}`;

  const normalizeImageUrl = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl === LEGACY_ECOPACK_IMAGE_URL) return undefined;
    return imageUrl;
  };

  const applyTemplateDefaults = (template: EcoPackTemplate): EcoPackTemplate => ({
    ...template,
    category: template.category || '에코팩',
    imageUrl: template.imageUrl,
    templateType: template.templateType || (template.size ? 'preset' : 'custom'),
    clientId: template.clientId || createClientId(),
    selected: template.selected ?? true
  });

  const sortTemplates = (list: EcoPackTemplate[]) => {
    const selected = list.filter((t) => t.selected !== false);
    const unselected = list.filter((t) => t.selected === false);
    return [...selected, ...unselected];
  };

  const normalizeTime = (time?: string | null) => {
    if (!time) return undefined;
    if (/^\d{2}:\d{2}$/.test(time)) return time;
    if (/^\d{2}:\d{2}:\d{2}/.test(time)) return time.slice(0, 5);
    return time;
  };

  // 레스토랑 ID 가져오기 및 기본값 계산
  useEffect(() => {
    const getRestaurantIdAndDefaults = async () => {
      const result = await sellerService.getSellerRestaurantId();
      if (result.success && result.restaurantId) {
        setRestaurantId(result.restaurantId);
        
        // 영업시간 기반 기본값 계산
        const defaults = await calculateDefaultPickupTimes(result.restaurantId);
        setDefaultPickupTimes(defaults);
        setPickupStartTime(defaults.start);
        setPickupEndTime(defaults.end);
      } else {
        showToast(result.error || '레스토랑 정보를 찾을 수 없습니다');
        setLoadingTemplates(false);
      }
    };
    getRestaurantIdAndDefaults();
  }, [showToast]);

  // 시간 관리 훅
  const {
    pickupStartTime,
    pickupEndTime,
    orderStartTime,
    orderEndTime,
    setPickupStartTime,
    setPickupEndTime,
    isValidTimeRange: isValidRange
  } = useDiscountTime({
    initialPickupStartTime: defaultPickupTimes.start,
    initialPickupEndTime: defaultPickupTimes.end,
    offerType: 'DEADLINE_DISCOUNT'
  });

  // 00:00~23:30 범위 전체를 옵션/스냅 대상으로 사용
  const timeOptions = useMemo(() => generateTimeOptions(0, 23), []);
  const toMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };
  const snapToOption = (time: string) => {
    if (!timeOptions.length) return time;
    if (timeOptions.includes(time)) return time;
    const target = toMinutes(time);
    let nearest = timeOptions[0];
    let diff = Math.abs(target - toMinutes(nearest));
    for (const opt of timeOptions) {
      const d = Math.abs(target - toMinutes(opt));
      if (d < diff) {
        diff = d;
        nearest = opt;
      }
    }
    return nearest;
  };

  useEffect(() => {
    const load = async () => {
      if (!restaurantId) return;
      setLoadingTemplates(true);
        const [templateResult, toggleResult, offersResult, orderWindowResult] = await Promise.all([
          sellerService.getDeadlineTemplateProducts(restaurantId),
          sellerService.getDeadlineDiscountToggle(restaurantId),
          sellerService.getProductOffers(restaurantId, 'DEADLINE_DISCOUNT', { includeInactive: true }),
          sellerService.getDeadlineOrderWindow(restaurantId)
        ]);

      if (toggleResult.success && typeof toggleResult.enabled === 'boolean') {
        setEnabled(toggleResult.enabled);
      }

      const offerMap = new Map<string, { rate: number; price: number }>();
      if (offersResult.success && offersResult.data) {
        offersResult.data.forEach((o: any) => {
          offerMap.set(o.product_id, {
            rate: o.discount_rate ?? defaultDiscountRate,
            price: o.discounted_price ?? 0
          });
        });
      }

      const fallbackTemplates: EcoPackTemplate[] = [
        { name: '에코팩 S', size: 'S', originalPrice: 2000, stock: 0, description: '', category: '에코팩', discountRate: defaultDiscountRate, discountedPrice: 2000 * 0.6, templateType: 'preset' },
        { name: '에코팩 M', size: 'M', originalPrice: 5000, stock: 0, description: '', category: '에코팩', discountRate: defaultDiscountRate, discountedPrice: 5000 * 0.6, templateType: 'preset' },
      ];

      if (templateResult.success && templateResult.data) {
        const mapped: EcoPackTemplate[] = templateResult.data.map((item: any) => {
          const name: string = item.name || '';
          const size: EcoPackTemplate['size'] = name.includes('XL')
            ? 'XL'
            : name.includes('L')
              ? 'L'
              : name.includes('M')
                ? 'M'
                : name.includes('S')
                  ? 'S'
                  : undefined;
          const templateType: EcoPackTemplate['templateType'] = size ? 'preset' : 'custom';
          const offer = offerMap.get(item.id);
          const rate = offer?.rate ?? defaultDiscountRate;
          const discountedPrice = offer?.price ?? Math.floor((item.original_price || 0) * (100 - rate) / 100);
          return {
            id: item.id,
            name: item.name,
            description: item.description || '',
            originalPrice: item.original_price || 0,
            stock: item.stock_quantity || 0,
            imageUrl: normalizeImageUrl(item.image_url),
            category: item.category || '에코팩',
            size,
            templateType,
            discountRate: rate,
            discountedPrice,
            selected: item.is_available ?? true
          };
        });
        const baseList = (mapped.length ? mapped : fallbackTemplates).map(applyTemplateDefaults);
        setTemplates(sortTemplates(baseList));
      } else {
        setTemplates(sortTemplates(fallbackTemplates.map(applyTemplateDefaults)));
      }

      // 기존에 저장된 마감할인 수령시간이 있으면 불러와서 적용
      if (offersResult.success && offersResult.data?.length) {
        const offerWithTimes = offersResult.data.find(
          (o) => o.deadline_pickup_start_time && o.deadline_pickup_end_time
        );

        const start = normalizeTime(offerWithTimes?.deadline_pickup_start_time);
        const end = normalizeTime(offerWithTimes?.deadline_pickup_end_time);

        const fallbackStart = defaultPickupTimes.start || '18:00';
        const fallbackEnd = defaultPickupTimes.end || '20:00';

        if (start && end && isValidTimeRangeFn(start, end)) {
          setPickupStartTime(snapToOption(start));
          setPickupEndTime(snapToOption(end));
        } else {
          console.warn('Invalid or missing pickup times, applying defaults', { start, end });
          setPickupStartTime(snapToOption(fallbackStart));
          setPickupEndTime(snapToOption(fallbackEnd));
        }
      }

      if (orderWindowResult.success && orderWindowResult.data) {
        const { useCustom, customOrderStartTime, customOrderEndTime } = orderWindowResult.data;
        setUseCustomOrderWindow(useCustom);
        if (useCustom) {
          setCustomOrderStartTime(normalizeTime(customOrderStartTime) || orderStartTime);
          setCustomOrderEndTime(normalizeTime(customOrderEndTime) || orderEndTime);
        } else {
          setCustomOrderStartTime(orderStartTime);
          setCustomOrderEndTime(orderEndTime);
        }
      } else {
        setUseCustomOrderWindow(false);
        setCustomOrderStartTime(orderStartTime);
        setCustomOrderEndTime(orderEndTime);
      }
      setLoadingTemplates(false);
    };
    load();
  }, [restaurantId]);

  const selectedTemplates = useMemo(
    () => templates.filter((t) => t.selected !== false),
    [templates]
  );
  const selectedCount = selectedTemplates.length;
  const hasInvalidSelected = selectedTemplates.some(t => !t.originalPrice || !t.stock);

  const handleSave = async () => {
    if (!restaurantId) return;
    const invalid = selectedTemplates.some(t => !t.originalPrice || !t.stock);
    if (invalid) {
      setShowValidation(true);
      showToast('가격과 수량은 필수입니다');
      return;
    }
    if (!isValidRange) {
      showToast('수령 가능 시간을 확인해주세요');
      return;
    }
    if (useCustomOrderWindow) {
      if (!customOrderStartTime || !customOrderEndTime || !isValidTimeRangeFn(customOrderStartTime, customOrderEndTime)) {
        showToast('주문 가능 시간을 확인해주세요');
        return;
      }
    }

    try {
      setSaving(true);
      const upsertResult = await sellerService.upsertDeadlineTemplateProducts(
        restaurantId,
        templates.map(t => ({
          id: t.id,
          name: t.name,
          description: t.description,
          price: t.originalPrice,
          stock: t.stock,
          category: t.category || '에코팩',
          isAvailable: t.selected !== false
        }))
      );
      if (!upsertResult.success || !upsertResult.productIds) throw new Error(upsertResult.error);

      for (const id of removedTemplateIds) {
        await sellerService.deleteDeadlineTemplateProduct(id);
      }

      const templateWithIds = templates.map((t, idx) => ({
        ...t,
        id: upsertResult.productIds?.[idx] || t.id,
        category: t.category || '에코팩',
        imageUrl: t.imageUrl,
        selected: t.selected ?? true
      }));

      const resolvedProductIds = templateWithIds.map(t => t.id).filter((id): id is string => Boolean(id));
      if (resolvedProductIds.length !== templateWithIds.length) {
        throw new Error('상품 ID 매핑에 실패했습니다');
      }

      const syncResult = await sellerService.syncDeadlineTemplateImages({
        templates: templateWithIds,
        productIds: resolvedProductIds,
      });

      if (!syncResult.success) throw new Error(syncResult.error);

      const activeTemplates = templateWithIds.filter((t) => t.selected !== false);
      const inactiveTemplates = templateWithIds.filter((t) => t.selected === false);

      await batchUpsertProductOffers(
        activeTemplates.map(t => ({
          productId: t.id!,
          discountRate: t.discountRate ?? defaultDiscountRate,
          discountedPrice: t.discountedPrice ?? Math.floor(t.originalPrice * (100 - (t.discountRate ?? defaultDiscountRate)) / 100),
          stock: t.stock,
          pickupStartTime,
          pickupEndTime
        })),
        'DEADLINE_DISCOUNT',
        restaurantId,
        {
          useCustomOrderWindow,
          customOrderStartTime: useCustomOrderWindow ? customOrderStartTime : undefined,
          customOrderEndTime: useCustomOrderWindow ? customOrderEndTime : undefined
        }
      );

      const today = getKoreanDate();
      for (const t of activeTemplates) {
        if (t.id) {
          await upsertProductStock(t.id, today, t.stock);
        }
      }

      const activeIds = activeTemplates.map(t => t.id!).filter(Boolean);
      const inactiveIds = inactiveTemplates.map(t => t.id!).filter(Boolean);
      const allIds = [...activeIds, ...inactiveIds];
      if (!enabled) {
        await setOffersActiveForProducts(allIds, 'DEADLINE_DISCOUNT', false);
        await sellerService.cancelDeadlineOrdersByProducts(restaurantId, allIds, '마감할인 비활성화로 인한 취소');
      } else {
        // 토글 ON 시 비활성화된 오퍼를 활성화해 노출 복구
        await setOffersActiveForProducts(inactiveIds, 'DEADLINE_DISCOUNT', false);
        await setOffersActiveForProducts(activeIds, 'DEADLINE_DISCOUNT', true);
      }

      await sellerService.updateDeadlineDiscountToggle(restaurantId, enabled);
      setShowValidation(false);

      showToast('마감할인 에코팩이 저장되었습니다');
      setTimeout(() => {
        navigate('/seller/store');
      }, 2000);
  } catch (e) {
    console.error(e);
    showToast('저장에 실패했습니다');
  } finally {
    setSaving(false);
  }
  };

  const effectiveOrderStartTime = useCustomOrderWindow
    ? (customOrderStartTime || orderStartTime)
    : orderStartTime;
  const effectiveOrderEndTime = useCustomOrderWindow
    ? (customOrderEndTime || orderEndTime)
    : orderEndTime;

  if (loadingTemplates) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/seller/store')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[32px] font-bold text-gray-800">오늘의 마감할인</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Product Selection */}
          <div className="lg:col-span-2 space-y-8">
            {/* 할인율 안내 */}
            <DiscountRateGuide type="deadline" />

            {/* 마감할인 운영 토글 */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div>
                <p className="text-sm text-gray-600">마감할인 운영</p>
                <p className="text-xs text-gray-500">off 시 오늘 마감할인이 비활성화되고 주문이 취소됩니다</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 relative">
                  <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition peer-checked:translate-x-5"></div>
                </div>
              </label>
            </div>

            <EcoPackTemplateSection
              templates={templates}
              onChange={(index, template) => {
                setTemplates(prev => sortTemplates(prev.map((item, i) => i === index ? template : item)));
              }}
              onAdd={(size) => {
                setTemplates(prev => sortTemplates([...prev, applyTemplateDefaults({
                  name: `에코팩 ${size}`,
                  size,
                  originalPrice: 0,
                  stock: 0,
                  description: '',
                  category: '에코팩',
                  discountRate: defaultDiscountRate,
                  discountedPrice: 0,
                  templateType: 'preset',
                  selected: true
                } as EcoPackTemplate)]));
              }}
              onAddCustom={() => {
                setTemplates(prev => sortTemplates([...prev, applyTemplateDefaults({
                  name: '',
                  originalPrice: 0,
                  stock: 0,
                  description: '',
                  category: '에코팩',
                  discountRate: defaultDiscountRate,
                  discountedPrice: 0,
                  templateType: 'custom',
                  selected: true
                } as EcoPackTemplate)]));
              }}
              onRemove={(index) => {
                setTemplates(prev => {
                  const target = prev[index];
                  if (target.id) setRemovedTemplateIds(ids => [...ids, target.id!]);
                  return sortTemplates(prev.filter((_, i) => i !== index));
                });
              }}
              showValidation={showValidation}
              fallbackImageUrl={DEFAULT_ECOPACK_IMAGE_URL}
            />
          </div>

          {/* Right Column - Settings & Actions */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[16px] font-semibold text-gray-800">주문가능시간 커스텀</p>
                  <p className="text-sm text-gray-500">수령시간과 별도로 주문 가능 시간을 설정합니다</p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={useCustomOrderWindow}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setUseCustomOrderWindow(next);
                      if (next) {
                        if (!customOrderStartTime) setCustomOrderStartTime(orderStartTime);
                        if (!customOrderEndTime) setCustomOrderEndTime(orderEndTime);
                      }
                    }}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 relative">
                    <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition peer-checked:translate-x-5"></div>
                  </div>
                </label>
              </div>

              {useCustomOrderWindow && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">주문 시작</label>
                    <select
                      value={customOrderStartTime}
                      onChange={(e) => setCustomOrderStartTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {timeOptions.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">주문 종료</label>
                    <select
                      value={customOrderEndTime}
                      onChange={(e) => setCustomOrderEndTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {timeOptions.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* 수령가능시간 설정 */}
            <TimeSettingSection
              type="deadline"
              pickupStartTime={pickupStartTime}
              pickupEndTime={pickupEndTime}
              onPickupStartTimeChange={(time) => setPickupStartTime(time)}
              onPickupEndTimeChange={(time) => setPickupEndTime(time)}
              orderStartTime={effectiveOrderStartTime}
              orderEndTime={effectiveOrderEndTime}
              showOrderHelper={!useCustomOrderWindow}
            />

            {/* 선택 요약 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-[20px] font-semibold text-gray-800 mb-4">선택 요약</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">선택된 상품</span>
                  <span className="text-[18px] font-semibold text-gray-800">{selectedCount}개</span>
                </div>
              </div>
            </div>

            {/* 저장 버튼 */}
            <div className="sticky top-6">
              <DiscountSaveButton
                type="deadline"
                canSave={templates.length > 0 && !hasInvalidSelected && isValidRange && !saving}
                saving={saving}
                selectedCount={selectedCount}
                onSave={handleSave}
              />
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}
